import React from 'react';
import {
  Target,
  ChartBar,
  ArrowRight,
  CircleDollarSign,
  Users,
  Building2,
  GitBranch,
  Globe,
  AlertTriangle,
  CheckCircle,
  Workflow,
  Database,
  Lock,
  Network,
  Smartphone,
  Brain
} from 'lucide-react';

// Composants de base
const Section = ({ title, icon: Icon, children, className = "" }) => (
  <div className={`mb-8 bg-white rounded-xl border border-gray-200 ${className}`}>
    <div className="p-6 border-b border-gray-100">
      <div className="flex items-center">
        <Icon className="w-6 h-6 text-blue-600 mr-3" />
        <h2 className="text-xl font-semibold">{title}</h2>
      </div>
    </div>
    <div className="p-6">{children}</div>
  </div>
);

const AnalysisPoint = ({ title, status = "info", children }) => {
  const statusColors = {
    success: "border-green-500 bg-green-50",
    warning: "border-yellow-500 bg-yellow-50",
    error: "border-red-500 bg-red-50",
    info: "border-blue-500 bg-blue-50"
  };

  return (
    <div className={`border-l-4 rounded-r-lg p-4 ${statusColors[status]}`}>
      <h3 className="font-medium mb-2">{title}</h3>
      <div className="text-sm space-y-2">{children}</div>
    </div>
  );
};

const Metric = ({ label, value, trend }) => (
  <div className="bg-gray-50 p-4 rounded-lg">
    <div className="text-sm text-gray-600 mb-1">{label}</div>
    <div className="text-2xl font-semibold">{value}</div>
    {trend && (
      <div className="text-sm text-gray-500 mt-1">{trend}</div>
    )}
  </div>
);

const CIDigitalFinance = () => {
  // Vision et Objectifs
  const vision = {
    overview: {
      title: "CI-Digital Finance",
      phase: "Démarrage",
      timeline: "2024-2027",
      status: "En cours d'initialisation"
    },
    objectives: [
      {
        title: "Infrastructure",
        items: [
          "Plateforme nationale de paiements",
          "Infrastructure cloud financière",
          "Hub d'interopérabilité",
          "Système KYC centralisé"
        ]
      },
      {
        title: "Services",
        items: [
          "Mobile banking unifié",
          "Paiements instantanés",
          "Inclusion financière digitale",
          "Open banking"
        ]
      },
      {
        title: "Innovation",
        items: [
          "Crédit scoring IA",
          "RegTech & SupTech",
          "Marketplace fintech",
          "API économie"
        ]
      }
    ]
  };

  // Architecture technique
  const technicalArchitecture = {
    components: [
      {
        name: "Plateforme Paiements",
        description: "Plateforme nationale de traitement des paiements",
        features: [
          "Switch interbancaire",
          "Rails paiements instantanés",
          "QR codes standards",
          "APIs ouvertes"
        ],
        technologies: [
          "Microservices",
          "Event streaming",
          "In-memory processing",
          "APIs RESTful"
        ]
      },
      {
        name: "Mobile Banking Hub",
        description: "Plateforme unifiée de services bancaires mobiles",
        features: [
          "Authentification unique",
          "Services financiers mobiles",
          "Gestion mandats",
          "Reporting unifié"
        ],
        technologies: [
          "Cloud native",
          "Conteneurisation",
          "Message Queue",
          "Cache distribué"
        ]
      },
      {
        name: "Crédit Scoring IA",
        description: "Plateforme d'évaluation crédit par IA",
        features: [
          "Scoring alternatif",
          "Analyse comportementale",
          "Prédiction risques",
          "Décision automatisée"
        ],
        technologies: [
          "Machine Learning",
          "Big Data Analytics",
          "APIs ML",
          "Data Lake"
        ]
      }
    ],
    standards: [
      {
        name: "Interopérabilité",
        specs: [
          "ISO 20022",
          "APIs REST",
          "OAuth 2.0",
          "JWT"
        ]
      },
      {
        name: "Sécurité",
        specs: [
          "PCI DSS",
          "ISO 27001",
          "KYC/AML standards",
          "NIST Framework"
        ]
      }
    ]
  };

  // Plan d'implémentation
  const implementationPlan = {
    phases: [
      {
        name: "Phase 1 - Foundation",
        period: "2024",
        deliverables: [
          "Infrastructure socle",
          "Cadre réglementaire",
          "Standards techniques",
          "Pilotes institutions"
        ],
        status: "En démarrage"
      },
      {
        name: "Phase 2 - Core Services",
        period: "2025",
        deliverables: [
          "Plateforme paiements",
          "Mobile banking unifié",
          "KYC digital",
          "APIs principales"
        ],
        status: "Planifié"
      },
      {
        name: "Phase 3 - Innovation",
        period: "2026-2027",
        deliverables: [
          "IA & Analytics",
          "Open banking",
          "Innovation hub",
          "Marketplace fintech"
        ],
        status: "Prévu"
      }
    ],
    governance: {
      committees: [
        "Comité pilotage national",
        "Comité technique",
        "Groupe régulateur",
        "Forum innovation"
      ],
      frequency: "Suivi mensuel",
      metrics: [
        "Adoption services",
        "Performance système",
        "Inclusion financière",
        "Satisfaction utilisateurs"
      ]
    }
  };

  // Impact attendu
  const impacts = {
    financial: [
      {
        metric: "Inclusion financière",
        target: "75%",
        baseline: "45%",
        timeline: "2027"
      },
      {
        metric: "Transactions digitales",
        target: "60%",
        baseline: "25%",
        timeline: "2027"
      },
      {
        metric: "Coût services",
        target: "-45%",
        baseline: "Base",
        timeline: "2027"
      }
    ],
    stakeholders: [
      {
        type: "Institutions financières",
        benefits: [
          "Réduction coûts opérationnels",
          "Innovation accélérée",
          "Reach étendu",
          "Efficacité accrue"
        ]
      },
      {
        type: "Consommateurs",
        benefits: [
          "Services accessibles",
          "Coûts réduits",
          "Expérience améliorée",
          "Choix élargi"
        ]
      },
      {
        type: "Fintech",
        benefits: [
          "Marché accessible",
          "Infrastructure mutualisée",
          "Innovation facilitée",
          "Time-to-market réduit"
        ]
      }
    ]
  };

  // Défis et risques
  const challenges = [
    {
      category: "Technique",
      items: [
        {
          challenge: "Infrastructure IT",
          impact: "Élevé",
          mitigation: "Partenariats stratégiques + Cloud"
        },
        {
          challenge: "Interopérabilité",
          impact: "Élevé",
          mitigation: "Standards ouverts + APIs"
        }
      ]
    },
    {
      category: "Opérationnel",
      items: [
        {
          challenge: "Adoption marché",
          impact: "Élevé",
          mitigation: "Programme accompagnement + Incitations"
        },
        {
          challenge: "Capacités secteur",
          impact: "Moyen",
          mitigation: "Formation + Support technique"
        }
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-2">
            CI-Digital Finance
          </h1>
          <div className="flex flex-wrap gap-4">
            <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm">
              Phase: {vision.overview.phase}
            </span>
            <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm">
              Timeline: {vision.overview.timeline}
            </span>
            <span className="px-3 py-1 bg-purple-100 text-purple-800 rounded-full text-sm">
              Status: {vision.overview.status}
            </span>
          </div>
        </div>

        {/* Vision et Objectifs */}
        <Section title="Vision et Objectifs" icon={Target}>
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {vision.objectives.map((obj, idx) => (
                <div key={idx} className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="font-medium mb-3">{obj.title}</h3>
                  <ul className="space-y-2">
                    {obj.items.map((item, i) => (
                      <li key={i} className="flex items-center text-sm">
                        <CheckCircle className="w-4 h-4 mr-2 text-green-500" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </Section>

        {/* Architecture Technique */}
        <Section title="Architecture Technique" icon={GitBranch}>
          <div className="space-y-6">
            {/* Composants principaux */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {technicalArchitecture.components.map((component, idx) => (
                <div key={idx} className="bg-gray-50 p-4 rounded-lg">
                  <div className="flex items-center mb-3">
                    <h3 className="font-medium">{component.name}</h3>
                  </div>
                  <p className="text-sm text-gray-600 mb-3">{component.description}</p>
                  <div className="space-y-3">
                    <div>
                      <h4 className="text-sm font-medium mb-2">Fonctionnalités:</h4>
                      <ul className="space-y-1">
                        {component.features.map((feature, i) => (
                          <li key={i} className="flex items-center text-sm">
                            <CheckCircle className="w-4 h-4 mr-2 text-green-500" />
                            {feature}
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <h4 className="text-sm font-medium mb-2">Technologies:</h4>
                      <div className="flex flex-wrap gap-2">
                        {component.technologies.map((tech, i) => (
                          <span key={i} className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs">
                            {tech}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Standards */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {technicalArchitecture.standards.map((standard, idx) => (
                <div key={idx} className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="font-medium mb-3">{standard.name}</h3>
                  <div className="flex flex-wrap gap-2">
                    {standard.specs.map((spec, i) => (
                      <span key={i} className="px-2 py-1 bg-gray-200 text-gray-700 rounded text-xs">
                        {spec}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </Section>

        {/* Plan d'implémentation */}
        <Section title="Plan d'Implémentation" icon={Workflow}>
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {implementationPlan.phases.map((phase, idx) => (
                <div key={idx} className="bg-gray-50 p-4 rounded-lg">
                  <div className="flex justify-between items-start mb-3">
                    <h3 className="font-medium">{phase.name}</h3>
                    <span className={`px-2 py-1 rounded text-xs ${
                      phase.status === 'En démarrage'
                        ? 'bg-blue-100 text-blue-700'
                        : phase.status === 'Planifié'
                          ? 'bg-yellow-100 text-yellow-700'
                          : 'bg-gray-100 text-gray-700'
                    }`}>
                      {phase.status}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 mb-3">Période: {phase.period}</p>
                  <ul className="space-y-1">
                    {phase.deliverables.map((deliverable, i) => (
                      <li key={i} className="flex items-center text-sm">
                        <ArrowRight className="w-4 h-4 mr-2 text-gray-400" />
                        {deliverable}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>

            {/* Gouvernance */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <h3 className="font-medium mb-4">Structure de Gouvernance</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="text-sm font-medium mb-2">Comités</h4>
                  <ul className="space-y-1">
                    {implementationPlan.governance.committees.map((committee, idx) => (
                      <li key={idx} className="flex items-center text-sm">
                        <Users className="w-4 h-4 mr-2 text-blue-500" />
                        {committee}
                      </li>
                    ))}
                  </ul>
                </div>
                <div>
                  <h4 className="text-sm font-medium mb-2">Métriques Clés</h4>
                  <ul className="space-y-1">
                    {implementationPlan.governance.metrics.map((metric, idx) => (
                      <li key={idx} className="flex items-center text-sm">
                        <ChartBar className="w-4 h-4 mr-2 text-green-500" />
                        {metric}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </Section>

        {/* Impact */}
        <Section title="Impact Attendu" icon={ChartBar}>
          <div className="space-y-6">
            {/* Métriques financières */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {impacts.financial.map((impact, idx) => (
                <Metric
                  key={idx}
                  label={impact.metric}
                  value={`${impact.baseline} → ${impact.target}`}
                  trend={`Objectif ${impact.timeline}`}
                />
              ))}
            </div>

            {/* Impact par partie prenante */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {impacts.stakeholders.map((stakeholder, idx) => (
                <div key={idx} className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="font-medium mb-3">{stakeholder.type}</h3>
                  <ul className="space-y-2">
                    {stakeholder.benefits.map((benefit, i) => (
                      <li key={i} className="flex items-center text-sm">
                        <CheckCircle className="w-4 h-4 mr-2 text-green-500" />
                        {benefit}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </Section>

        {/* Défis */}
        <Section title="Défis et Risques" icon={AlertTriangle}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {challenges.map((category, idx) => (
              <div key={idx}>
                <h3 className="font-medium mb-4">{category.category}</h3>
                <div className="space-y-4">
                  {category.items.map((item, i) => (
                    <div key={i} className="bg-white p-4 rounded-lg border border-gray-200">
                      <div className="flex justify-between items-center mb-2">
                        <span className="font-medium">{item.challenge}</span>
                        <span className={`px-2 py-1 rounded text-xs ${
                          item.impact === 'Élevé'
                            ? 'bg-red-100 text-red-700'
                            : 'bg-yellow-100 text-yellow-700'
                        }`}>
                          Impact: {item.impact}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600">
                        Mitigation: {item.mitigation}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </Section>
      </div>
    </div>
  );
};

export default CIDigitalFinance;